import React from 'react';
import constants from '../constants/constants';

const { appointmentPhases } = constants;

/** Fixed semantic order 0–11; named phase colors only (not Object.values / not phase ids). */
export const DOCTOR_COLOR_BY_INDEX = [
  appointmentPhases.expected.color,
  appointmentPhases.checkIn.color,
  appointmentPhases.diagnostics.color,
  appointmentPhases.preparation.color,
  appointmentPhases.procedure.color,
  appointmentPhases.dental.color,
  appointmentPhases.surgery.color,
  appointmentPhases.recovery.color,
  appointmentPhases.hospitalized.color,
  appointmentPhases.boarding.color,
  appointmentPhases.discharge.color,
  appointmentPhases.completed.color,
];

export const STAFF_DOCTOR_OPTION_CLASS = 'staff-doctor-select__option';
export const STAFF_DOCTOR_OPTION_CONTENT_CLASS =
  'staff-doctor-select__option-content';
export const STAFF_DOCTOR_CIRCLE_CLASS = 'staff-doctor-select__circle';
export const STAFF_DOCTOR_NAME_CLASS = 'staff-doctor-select__name';

const VIEWPORT_MENU_GUTTER_PX = 24;

export function getDoctorColor(doctorColorIndex) {
  if (
    doctorColorIndex === null ||
    doctorColorIndex === undefined ||
    doctorColorIndex === ''
  ) {
    return null;
  }
  const index = Number(doctorColorIndex);
  if (!Number.isInteger(index) || index < 0 || index > 11) {
    return null;
  }
  return DOCTOR_COLOR_BY_INDEX[index] || null;
}

export function getDoctorInitial({ lastName, firstName } = {}) {
  const last = String(lastName || '').trim();
  if (last) {
    return last.charAt(0).toUpperCase();
  }
  const first = String(firstName || '').trim();
  if (first) {
    return first.charAt(0).toUpperCase();
  }
  return '?';
}

/**
 * Map GET /v1/staff-doctors/by-facility rows to react-select options.
 * Carries lastName + doctorColorIndex for colored initials (never avatar).
 */
export function mapStaffDoctorsFromApi(list) {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map((doctor) => {
    const value = doctor.doctorId || doctor._id;
    return {
      value,
      label: doctor.name || '',
      lastName: doctor.lastName || '',
      firstName: doctor.firstName || '',
      doctorColorIndex:
        doctor.doctorColorIndex === null ||
        doctor.doctorColorIndex === undefined
          ? null
          : doctor.doctorColorIndex,
      isNaOption: false,
    };
  });
}

/** Appointment Request assignment options: leading N/A (plain), no auto "Dr.". */
export function mapAppointmentRequestDoctorOptions(list) {
  return [
    {
      value: '',
      label: 'N/A',
      lastName: '',
      firstName: '',
      doctorColorIndex: null,
      isNaOption: true,
    },
    ...mapStaffDoctorsFromApi(list),
  ];
}

export function formatStaffDoctorOptionLabel(option) {
  if (!option) {
    return null;
  }

  const label = option.label || '';

  if (option.isNaOption) {
    return (
      <span className={STAFF_DOCTOR_OPTION_CONTENT_CLASS}>
        <span className={STAFF_DOCTOR_NAME_CLASS}>{label}</span>
      </span>
    );
  }

  const color = getDoctorColor(option.doctorColorIndex);
  if (!color) {
    return (
      <span className={STAFF_DOCTOR_OPTION_CONTENT_CLASS}>
        <span className={STAFF_DOCTOR_NAME_CLASS}>{label}</span>
      </span>
    );
  }

  const initial = getDoctorInitial(option);

  return (
    <span className={STAFF_DOCTOR_OPTION_CONTENT_CLASS}>
      <span
        className={STAFF_DOCTOR_CIRCLE_CLASS}
        style={{ backgroundColor: color }}
        aria-hidden="true"
      >
        {initial}
      </span>
      <span className={STAFF_DOCTOR_NAME_CLASS}>{label}</span>
    </span>
  );
}

function mergeStyleFns(baseFn, nextFn) {
  if (!baseFn) {
    return nextFn;
  }
  if (!nextFn) {
    return baseFn;
  }
  return (base, state) => nextFn(baseFn(base, state), state);
}

/**
 * Menu: at least control width, grow for full name, no wrap, viewport-capped.
 * Merge with each surface's existing react-select `styles`.
 */
export function mergeStaffDoctorSelectStyles(existingStyles = {}) {
  const menuMaxWidth = `calc(100vw - ${VIEWPORT_MENU_GUTTER_PX}px)`;

  return {
    ...existingStyles,
    menu: mergeStyleFns(existingStyles.menu, (base) => ({
      ...base,
      width: 'max-content',
      minWidth: '100%',
      maxWidth: menuMaxWidth,
    })),
    option: mergeStyleFns(existingStyles.option, (base) => ({
      ...base,
      padding: 0,
      whiteSpace: 'nowrap',
    })),
    singleValue: mergeStyleFns(existingStyles.singleValue, (base) => ({
      ...base,
      maxWidth: '100%',
      overflow: 'visible',
    })),
  };
}

export const staffDoctorSelectSharedProps = {
  formatOptionLabel: formatStaffDoctorOptionLabel,
  className: 'staff-doctor-select',
  classNames: {
    option: () => STAFF_DOCTOR_OPTION_CLASS,
  },
};
